import { Check, ExternalLink, Mail } from "lucide-react";
import { SITE, DISCLAIMER_LONG } from "@/lib/site";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const INCLUDED = [
  "Six weeks of live, education-based online sessions",
  "Once-weekly meetings led by Aimee Page, MS LPC NCC",
  "A structured curriculum on nervous system regulation",
  "Skill-building exercises for daily, ordinary life",
  "Replays of teaching segments for the duration of the cohort",
  "A small, focused cohort — not a stadium webinar",
];

export default function Enroll() {
  return (
    <div className="rr-reveal">
      <section className="pt-24 md:pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p
              className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
              style={{ color: "var(--rr-primary)" }}
            >
              Enrollment
            </p>
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight"
              style={{ color: "var(--rr-text)" }}
              data-testid="enroll-title"
            >
              Reserve your seat.
            </h1>
            <p
              className="mt-8 max-w-xl text-lg leading-relaxed"
              style={{ color: "var(--rr-text-2)" }}
            >
              One simple, one-time payment of {SITE.priceLabel} covers the full six-week program. Payment is handled securely through PayPal.
            </p>

            <div className="mt-14">
              <p
                className="text-xs tracking-[0.2em] uppercase mb-6"
                style={{ color: "var(--rr-primary)" }}
              >
                What’s included
              </p>
              <ul className="space-y-3">
                {INCLUDED.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`included-item-${i}`}
                  >
                    <Check
                      size={18}
                      strokeWidth={1.5}
                      className="shrink-0 mt-1"
                      style={{ color: "var(--rr-primary)" }}
                    />
                    <span
                      className="text-base leading-relaxed"
                      style={{ color: "var(--rr-text)" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pricing card — cleaner, less ornamentation */}
          <div className="lg:col-span-5">
            <div
              className="border p-8 sm:p-10 lg:sticky lg:top-28"
              style={{
                background: "var(--rr-bg)",
                borderColor: "var(--rr-border)",
              }}
              data-testid="pricing-card"
            >
              <p
                className="text-xs tracking-[0.2em] uppercase"
                style={{ color: "var(--rr-primary)" }}
              >
                Full Program
              </p>
              <div className="mt-5 flex items-baseline gap-3">
                <span
                  className="font-serif text-6xl leading-none"
                  style={{ color: "var(--rr-text)" }}
                >
                  {SITE.priceLabel}
                </span>
                <span className="text-sm" style={{ color: "var(--rr-text-2)" }}>
                  USD · one-time
                </span>
              </div>
              <p className="mt-3 text-sm" style={{ color: "var(--rr-text-2)" }}>
                Six weekly live sessions · {SITE.course.weeks} weeks total
              </p>

              <div
                className="mt-6 pt-6 border-t flex items-baseline justify-between gap-4"
                style={{ borderColor: "var(--rr-border)" }}
              >
                <span
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ color: "var(--rr-primary)" }}
                >
                  Cohort begins
                </span>
                <span
                  className="font-serif text-xl"
                  style={{ color: "var(--rr-text)" }}
                  data-testid="enroll-start-date"
                >
                  {SITE.course.startDate}
                </span>
              </div>

              <a
                href={SITE.paypalUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paypal-checkout-button"
                className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-sm font-semibold transition-colors"
                style={{ background: "#FFC439", color: "#142C8E" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F0B92E")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#FFC439")}
              >
                Pay with PayPal
                <ExternalLink size={15} />
              </a>

              <p
                className="mt-4 text-xs leading-relaxed"
                style={{ color: "var(--rr-text-2)" }}
              >
                You’ll be redirected to PayPal to complete payment securely.
              </p>

              <div
                className="mt-8 pt-6 border-t"
                style={{ borderColor: "var(--rr-border)" }}
              >
                <p
                  className="text-xs tracking-wide uppercase mb-3"
                  style={{ color: "var(--rr-primary)" }}
                >
                  Questions before enrolling?
                </p>
                <a
                  href={`mailto:${SITE.email}`}
                  className="inline-flex items-center gap-2 text-sm rr-link"
                  style={{ color: "var(--rr-text)" }}
                  data-testid="enroll-email-link"
                >
                  <Mail size={15} />
                  {SITE.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DisclaimerBanner testid="enroll-disclaimer-banner" />

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12">
          <p
            className="text-xs tracking-[0.2em] uppercase mb-4"
            style={{ color: "var(--rr-primary)" }}
          >
            Full disclaimer
          </p>
          <p className="text-sm leading-relaxed" style={{ color: "var(--rr-text-2)" }}>
            {DISCLAIMER_LONG}
          </p>
        </div>
      </section>
    </div>
  );
}
